import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

// ─── WIZ Products ─────────────────────────────────────────────────────────────
const PRODUCTS = [
  {
    id: 1,
    name: 'DineOS',
    tagline: 'Restaurant Intelligence Platform',
    color: '#45d0bd',
    icon: '🍽️',
    description: 'The complete AI-powered operating system for modern restaurants. From smart self-ordering kiosks to real-time kitchen displays, inventory forecasting, and financial analytics — all in one platform.',
    highlights: [
      'AI demand forecasting reduces food waste by 45%',
      'Touch-screen self-ordering kiosks',
      'Kitchen Display System (KDS)',
      'Multi-branch management dashboard',
      'Integrated payment gateway',
      'Staff & shift scheduling',
    ],
    stats: [{ value: '30%', label: 'Cost Savings' }, { value: '3×', label: 'Faster Orders' }, { value: '8+', label: 'Branches Served' }],
    demoUrl: 'https://pos.wiz-tech.co',
    demoLabel: 'Live POS Demo',
    image: '/assets/images/products/pos-system.png',
    screens: [
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.32.png',
    ],
    industries: ['QSR Chains', 'Cloud Kitchens', 'Casual Dining', 'Cafes'],
  },
  {
    id: 2,
    name: 'QLick Health',
    tagline: 'Healthcare Management System',
    color: '#8258c8',
    icon: '🏥',
    description: 'A comprehensive, medical-grade platform for clinics and hospitals — covering patient management, appointment scheduling, doctor portals, and AI-assisted diagnostics under one roof.',
    highlights: [
      'Zero-error collision-proof appointment engine',
      'Patient record management with history',
      'Doctor content & education publishing',
      'Mobile app for patients (iOS & Android)',
      'Role-based staff access control',
      'Autism therapy robot integration',
    ],
    stats: [{ value: '0', label: 'Appointment Errors' }, { value: '60%', label: 'Less Admin Work' }, { value: '4.9★', label: 'Staff Rating' }],
    demoUrl: 'https://qlickhealth.ai',
    demoLabel: 'Visit QLick Health',
    image: '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
    screens: [
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.12.10.png',
    ],
    industries: ['Clinics', 'Hospitals', 'Therapy Centers', 'Wellness'],
  },
  {
    id: 3,
    name: 'Safqa',
    tagline: 'Smart Invoice & Business Finance',
    color: '#2c84c8',
    icon: '💳',
    description: 'A powerful mobile-first invoicing and financial management app for freelancers, SMEs, and growing businesses. Create, send, and track invoices with real-time payment status and financial reporting.',
    highlights: [
      'Create & send professional invoices in seconds',
      'Multi-currency & VAT support',
      'Real-time payment tracking',
      'Client management database',
      'Financial analytics & reporting',
      'Available on iOS & Android',
    ],
    stats: [{ value: '500+', label: 'Active Users' }, { value: '< 30s', label: 'Invoice to Send' }, { value: '100%', label: 'Mobile-First' }],
    demoUrl: 'https://safqa.wiz-tech.co',
    demoLabel: 'Try Safqa',
    image: '/assets/images/products/ondemand/MAIN.png',
    screens: [
      '/assets/images/products/ondemand/Android Compact - 1.png',
      '/assets/images/products/ondemand/Android Compact - 2.png',
    ],
    industries: ['Freelancers', 'SMEs', 'Consultants', 'Service Businesses'],
  },
  {
    id: 4,
    name: 'Collector',
    tagline: 'AI Meter Reading & Field Operations',
    color: '#45d0bd',
    icon: '⚡',
    description: 'An AI-powered mobile solution for utility companies — automate meter reading collection using OCR and computer vision, eliminate manual data entry errors, and get real-time field operations dashboards.',
    highlights: [
      'AI + OCR automatic meter reading',
      'Offline-first for remote field use',
      'Real-time sync and dashboard',
      'GPS tracking of field agents',
      'Billing analytics & anomaly detection',
      'Multi-region & multi-utility support',
    ],
    stats: [{ value: '99%', label: 'Reading Accuracy' }, { value: '80%', label: 'Less Field Time' }, { value: 'Zero', label: 'Manual Entry' }],
    demoUrl: 'https://collector.wiz-tech.co',
    demoLabel: 'View Collector Demo',
    image: '/assets/images/products/ondemand/Demo 22.png',
    screens: [
      '/assets/images/products/ondemand/Android Compact - 3.png',
      '/assets/images/products/ondemand/Android Compact - 4.png',
    ],
    industries: ['Electricity Utilities', 'Water Utilities', 'Gas Companies', 'Municipalities'],
  },
];

// ─── Arabic Products ──────────────────────────────────────────────────────────
const PRODUCTS_AR = [
  {
    id: 1,
    name: 'DineOS',
    tagline: 'منصة ذكاء المطاعم',
    color: '#45d0bd',
    icon: '🍽️',
    description: 'نظام التشغيل الكامل المدعوم بالذكاء الاصطناعي للمطاعم الحديثة. من أكشاك الطلب الذاتي الذكية إلى شاشات المطبخ الآنية وتوقع المخزون والتحليلات المالية — كل ذلك في منصة واحدة.',
    highlights: [
      'التوقع بالطلب بالذكاء الاصطناعي يقلل الهدر بنسبة 45٪',
      'أكشاك طلب ذاتي بشاشة لمس',
      'نظام عرض المطبخ (KDS)',
      'لوحة إدارة متعددة الفروع',
      'بوابة دفع متكاملة',
      'إدارة الموظفين والورديات',
    ],
    stats: [{ value: '30%', label: 'توفير التكاليف' }, { value: '3×', label: 'طلبات أسرع' }, { value: '8+', label: 'فروع مخدومة' }],
    demoUrl: 'https://pos.wiz-tech.co',
    demoLabel: 'عرض POS مباشر',
    image: '/assets/images/products/pos-system.png',
    screens: [
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.29.png',
      '/assets/images/projects/DineOS/Screenshot 2024-04-25 at 2.03.32.png',
    ],
    industries: ['سلاسل المطاعم السريعة', 'المطابخ السحابية', 'المطاعم العادية', 'المقاهي'],
  },
  {
    id: 2,
    name: 'QLick Health',
    tagline: 'نظام إدارة الرعاية الصحية',
    color: '#8258c8',
    icon: '🏥',
    description: 'منصة طبية شاملة للعيادات والمستشفيات — تغطي إدارة المرضى وجدولة المواعيد وبوابات الأطباء والتشخيص بمساعدة الذكاء الاصطناعي تحت سقف واحد.',
    highlights: [
      'محرك حجز مواعيد خالٍ من التعارضات',
      'إدارة سجلات المرضى بالتاريخ الكامل',
      'نشر محتوى طبي وتعليمي للأطباء',
      'تطبيق جوال للمرضى (iOS وAndroid)',
      'تحكم في الوصول حسب دور الموظف',
      'تكامل روبوت علاج التوحد',
    ],
    stats: [{ value: '0', label: 'أخطاء مواعيد' }, { value: '60%', label: 'تقليل الأعمال الإدارية' }, { value: '4.9★', label: 'تقييم الموظفين' }],
    demoUrl: 'https://qlickhealth.ai',
    demoLabel: 'زيارة QLick Health',
    image: '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
    screens: [
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.12.10.png',
    ],
    industries: ['العيادات', 'المستشفيات', 'مراكز العلاج', 'الرعاية الصحية'],
  },
  {
    id: 3,
    name: 'Safqa',
    tagline: 'الفواتير الذكية وإدارة الأعمال',
    color: '#2c84c8',
    icon: '💳',
    description: 'تطبيق متكامل للفوترة والإدارة المالية للمستقلين والشركات الصغيرة. أنشئ الفواتير وأرسلها وتتبعها مع حالة الدفع الفورية والتقارير المالية.',
    highlights: [
      'إنشاء وإرسال فواتير احترافية في ثوانٍ',
      'دعم متعدد العملات وضريبة القيمة المضافة',
      'تتبع الدفع الفوري',
      'قاعدة بيانات إدارة العملاء',
      'تحليلات وتقارير مالية',
      'متاح على iOS وAndroid',
    ],
    stats: [{ value: '500+', label: 'مستخدم نشط' }, { value: '< 30 ث', label: 'من الفاتورة للإرسال' }, { value: '100%', label: 'أولوية الجوال' }],
    demoUrl: 'https://safqa.wiz-tech.co',
    demoLabel: 'تجربة Safqa',
    image: '/assets/images/products/ondemand/MAIN.png',
    screens: [
      '/assets/images/products/ondemand/Android Compact - 1.png',
      '/assets/images/products/ondemand/Android Compact - 2.png',
    ],
    industries: ['المستقلون', 'الشركات الصغيرة', 'الاستشاريون', 'الخدمات'],
  },
  {
    id: 4,
    name: 'Collector',
    tagline: 'قراءة العدادات الذكية بالذكاء الاصطناعي',
    color: '#45d0bd',
    icon: '⚡',
    description: 'حل جوال مدعوم بالذكاء الاصطناعي لشركات المرافق — أتمتة جمع قراءات العدادات باستخدام OCR والرؤية الحاسوبية، والتخلص من أخطاء الإدخال اليدوي، مع لوحات عمليات ميدانية فورية.',
    highlights: [
      'قراءة تلقائية للعدادات بالذكاء الاصطناعي وOCR',
      'يعمل دون اتصال للاستخدام الميداني النائي',
      'مزامنة فورية ولوحة تحكم',
      'تتبع GPS للوكلاء الميدانيين',
      'تحليلات الفواتير واكتشاف الشذوذ',
      'دعم متعدد المناطق والمرافق',
    ],
    stats: [{ value: '99%', label: 'دقة القراءة' }, { value: '80%', label: 'تقليل وقت الميدان' }, { value: 'صفر', label: 'إدخال يدوي' }],
    demoUrl: 'https://collector.wiz-tech.co',
    demoLabel: 'عرض Collector',
    image: '/assets/images/products/ondemand/Demo 22.png',
    screens: [
      '/assets/images/products/ondemand/Android Compact - 3.png',
      '/assets/images/products/ondemand/Android Compact - 4.png',
    ],
    industries: ['شركات الكهرباء', 'شركات المياه', 'شركات الغاز', 'البلديات'],
  },
];

// ─── Additional Platforms ─────────────────────────────────────────────────────
const DEMOS = [
  {
    name: 'Seating Platform',
    description: 'Event seating & VIP ticketing system — used at FIFA Arab Cup Qatar.',
    url: 'https://seating.wiz-tech.co',
    icon: '🏟️',
    color: '#45d0bd',
  },
  {
    name: 'Smart Ledger Demo',
    description: 'AI financial analytics and account management dashboard.',
    url: 'https://demo.wiz-tech.co',
    icon: '📊',
    color: '#8258c8',
  },
];

const DEMOS_AR = [
  {
    name: 'منصة الجلوس',
    description: 'نظام حجز المقاعد وتذاكر VIP — مستخدم في كأس العرب FIFA قطر.',
    url: 'https://seating.wiz-tech.co',
    icon: '🏟️',
    color: '#45d0bd',
  },
  {
    name: 'عرض Smart Ledger',
    description: 'لوحة تحليلات مالية بالذكاء الاصطناعي وإدارة الحسابات.',
    url: 'https://demo.wiz-tech.co',
    icon: '📊',
    color: '#8258c8',
  },
];

const glass = {
  background: 'rgba(10, 10, 18, 0.58)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
};

const ProductCard = ({ product, onSelect, isActive }) => (
  <div
    onClick={() => onSelect(product)}
    style={{
      ...glass, padding: '28px', cursor: 'pointer',
      borderTop: `3px solid ${isActive ? product.color : 'transparent'}`,
      transition: 'all 0.3s',
      opacity: isActive ? 1 : 0.75,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
      <div style={{
        width: 50, height: 50, borderRadius: 14, fontSize: '1.4rem',
        background: `${product.color}15`, border: `1px solid ${product.color}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {product.icon}
      </div>
      <div>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>{product.name}</div>
        <div style={{ color: product.color, fontSize: '11px', fontWeight: 700 }}>{product.tagline}</div>
      </div>
    </div>
    <p style={{ color: '#6b6b88', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: 16 }}>
      {product.description.slice(0, 120)}…
    </p>
    <div style={{ display: 'flex', gap: 14 }}>
      {product.stats.map((s, i) => (
        <div key={i} style={{ textAlign: 'center' }}>
          <div style={{ color: product.color, fontWeight: 800, fontSize: '1.2rem' }}>{s.value}</div>
          <div style={{ color: '#6b6b88', fontSize: '10px' }}>{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const Products = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const getPath = useLangPath();
  const products = isAr ? PRODUCTS_AR : PRODUCTS;
  const demos = isAr ? DEMOS_AR : DEMOS;
  const [selected, setSelected] = useState(products[0]);

  // Reset selection when language changes to show first product of new language
  React.useEffect(() => {
    setSelected(products[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAr]);

  return (
    <>
      {/* ── Hero ── */}
      <section style={{ padding: '170px 0 60px', textAlign: 'center', background: 'transparent' }}>
        <div className="container">
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(130,88,200,0.12)', border: '1px solid rgba(130,88,200,0.3)',
            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8258c8', display: 'inline-block' }} />
            <span style={{ color: '#8258c8', fontSize: '13px', fontWeight: 600 }}>{isAr ? '4 منتجات أساسية' : '4 Core Products'}</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2.2rem, 5vw, 3.6rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: 18 }}>
            {isAr ? <>منصاتنا <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>المتقدمة</span></> : <>Our <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Platforms</span></>}
          </h1>
          <p style={{ color: '#6b6b88', maxWidth: '580px', margin: '0 auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
            {isAr ? 'منتجات قابلة للتوسع ومدعومة بالذكاء الاصطناعي من WIZ — كل منها يحل تحديًا صناعيًا حقيقيًا بدقة وموثوقية.' : 'Scalable, AI-powered products engineered by WIZ — each solving a real industry challenge with precision and reliability.'}
          </p>
        </div>
      </section>

      {/* ── Product Cards Grid ── */}
      <section style={{ background: 'transparent', padding: '0 0 60px' }}>
        <div className="container">
          <div className="row g-4">
            {products.map(p => (
              <div className="col-md-6 col-xl-3" key={p.id}>
                <ProductCard product={p} onSelect={setSelected} isActive={selected.id === p.id} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Selected Product Deep-Dive ── */}
      <section style={{ background: 'transparent', padding: '0 0 80px' }}>
        <div className="container">
          <div style={{ ...glass, overflow: 'hidden' }}>
            <div className="row g-0">
              {/* Left visual */}
              <div className="col-lg-5" style={{
                background: `linear-gradient(135deg, ${selected.color}14 0%, #0a0a1800 100%)`,
                borderRight: `1px solid rgba(255,255,255,0.06)`,
                padding: '48px 40px',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}>
                {/* Product header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 18, fontSize: '2rem',
                    background: `${selected.color}15`, border: `1px solid ${selected.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {selected.icon}
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.6rem' }}>{selected.name}</div>
                    <div style={{ color: selected.color, fontSize: '13px', fontWeight: 700 }}>{selected.tagline}</div>
                  </div>
                </div>

                {/* Screenshots */}
                {selected.screens.length > 0 && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
                    {selected.screens.slice(0, 2).map((s, i) => (
                      <div key={i} style={{
                        flex: 1, height: 180, borderRadius: 14, overflow: 'hidden',
                        border: `1px solid ${selected.color}25`,
                      }}>
                        <img src={s} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Stats */}
                <div style={{ display: 'flex', gap: 20 }}>
                  {selected.stats.map((s, i) => (
                    <div key={i} style={{
                      textAlign: 'center', padding: '12px 16px',
                      background: `${selected.color}10`,
                      border: `1px solid ${selected.color}20`,
                      borderRadius: 12,
                    }}>
                      <div style={{
                        fontSize: '1.5rem', fontWeight: 900,
                        background: `linear-gradient(135deg, ${selected.color}, ${selected.color}88)`,
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      }}>{s.value}</div>
                      <div style={{ color: '#6b6b88', fontSize: '10px', marginTop: 4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right content */}
              <div className="col-lg-7" style={{ padding: '48px 40px' }}>
                <p style={{ color: '#8888aa', fontSize: '1rem', lineHeight: 1.8, marginBottom: 28 }}>
                  {selected.description}
                </p>

                {/* Highlights */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 14, textTransform: 'uppercase' }}>
                    {isAr ? 'مميزات المنصة' : 'Platform Features'}
                  </div>
                  <div className="row g-2">
                    {selected.highlights.map((h, i) => (
                      <div className="col-12 col-sm-6" key={i}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{
                            width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                            background: `${selected.color}18`, border: `1px solid ${selected.color}35`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={selected.color} strokeWidth="3">
                              <path d="M5 12l5 5 9-9"/>
                            </svg>
                          </div>
                          <span style={{ color: '#ccccdd', fontSize: '0.88rem', lineHeight: 1.5 }}>{h}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Industries */}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12, textTransform: 'uppercase' }}>
                    {isAr ? 'القطاعات المخدومة' : 'Industries Served'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selected.industries.map(ind => (
                      <span key={ind} style={{
                        fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20,
                        background: `${selected.color}12`, border: `1px solid ${selected.color}28`,
                        color: selected.color,
                      }}>{ind}</span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <a
                    href={selected.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: `linear-gradient(102deg, ${selected.color}dd, ${selected.color}99)`,
                      color: '#fff', padding: '12px 28px', borderRadius: 50,
                      textDecoration: 'none', fontWeight: 700, fontSize: 14,
                      boxShadow: `0 8px 24px ${selected.color}30`,
                    }}
                  >
                    {selected.demoLabel}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                  <Link
                    to={getPath('/contact')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(14px)',
                      border: '1.5px solid rgba(130,88,200,0.35)',
                      color: '#fff', padding: '12px 28px', borderRadius: 50,
                      textDecoration: 'none', fontWeight: 600, fontSize: 14,
                    }}
                  >
                    {isAr ? 'طلب عرض تجريبي' : 'Request a Demo'}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── More Demos ── */}
      <section style={{ background: 'transparent', padding: '0 0 80px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h3 style={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem', marginBottom: 8 }}>
              {isAr ? 'منصات حية إضافية' : 'More Live Platforms'}
            </h3>
            <p style={{ color: '#6b6b88', fontSize: '0.9rem' }}>{isAr ? 'منصات WIZ إضافية متاحة للعروض التجريبية المباشرة' : 'Additional WIZ platforms available for live demos'}</p>
          </div>
          <div className="row g-4 justify-content-center">
            {demos.map(demo => (
              <div className="col-md-5" key={demo.name}>
                <a
                  href={demo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <div style={{
                    ...glass, padding: '28px',
                    display: 'flex', alignItems: 'center', gap: 18,
                    transition: 'all 0.3s',
                    borderTop: `2px solid ${demo.color}44`,
                  }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, fontSize: '1.5rem',
                      background: `${demo.color}15`, border: `1px solid ${demo.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {demo.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>{demo.name}</div>
                      <div style={{ color: '#6b6b88', fontSize: '0.85rem', lineHeight: 1.5 }}>{demo.description}</div>
                    </div>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: `${demo.color}15`, border: `1px solid ${demo.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={demo.color} strokeWidth="2.2">
                        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        background: 'rgba(130,88,200,0.06)', backdropFilter: 'blur(20px)',
        padding: '80px 0', textAlign: 'center',
        borderTop: '1px solid rgba(130,88,200,0.15)',
      }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 16, fontSize: 'clamp(1.8rem,4vw,2.4rem)', fontWeight: 700 }}>
            {isAr ? 'تحتاج حلاً مخصصاً؟' : 'Need a custom solution?'}
          </h2>
          <p style={{ color: '#6b6b88', marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}>
            {isAr ? 'تبني WIZ منصات مخصصة تتناسب مع صناعتك وحجمك وسير عملك — ليست برامج جاهزة.' : 'WIZ builds bespoke platforms tailored to your industry, scale, and workflow — not off-the-shelf software.'}
          </p>
          <Link to={getPath('/contact')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
            color: '#fff', padding: '14px 36px', borderRadius: 50,
            textDecoration: 'none', fontWeight: 700, fontSize: 15,
            boxShadow: '0 8px 28px rgba(130,88,200,0.35)',
          }}>
            {isAr ? 'تحدث مع WIZ ←' : 'Talk to WIZ →'}
          </Link>
        </div>
      </section>
    </>
  );
};

export default Products;
